<template>
  <div
    v-if="measurement"
    class="measurement-list-item"
  >
    <div
      class="measurement-list-item__section measurement-list-item__section--saddle"
    >
      <div class="measurement-list-item__row">
        <span class="measurement-list-item__column-header">{{
          t('common.bike.saddle')
        }}</span>
      </div>
      <div class="measurement-list-item__row">
        <strong>
          <span v-if="measurement.saddle.active"
            >{{ measurement.saddle.active.SADDLE_BRAND }}
            {{ measurement.saddle.active.SADDLE_MODEL }}</span
          >
          <span v-else>-</span>
        </strong>
      </div>
      <div class="measurement-list-item__row">
        <label>{{ t('common.position.x') }}</label>
        <span
          >{{ `${measurement.saddle.x} ${t('common.unit.mm')}` }}
          <span class="measurement-list-item__difference"
            >({{
              `${measurement.saddle.x - firstMeasurement.saddle.x} ${t('common.unit.mm')}`
            }})</span
          ></span
        >
      </div>
      <div class="measurement-list-item__row">
        <label>{{ t('common.position.y') }}</label>
        <span
          >{{ `${measurement.saddle.y} ${t('common.unit.mm')}` }}
          <span class="measurement-list-item__difference"
            >({{
              `${measurement.saddle.y - firstMeasurement.saddle.y} ${t('common.unit.mm')}`
            }})</span
          ></span
        >
      </div>
    </div>

    <div
      class="measurement-list-item__section measurement-list-item__section--handlebar"
    >
      <div class="measurement-list-item__row">
        <span class="measurement-list-item__column-header">{{
          t('common.bike.handlebar')
        }}</span>
      </div>
      <div class="measurement-list-item__row">
        <strong>
          <span v-if="measurement.handlebar.active"
            >{{ measurement.handlebar.active.HANDLEBAR_BRAND }}
            {{ measurement.handlebar.active.HANDLEBAR_MODEL }}</span
          >
          <span v-else>-</span>
        </strong>
      </div>
      <div class="measurement-list-item__row">
        <label>{{ t('common.position.x') }}</label>
        <span
          >{{ `${measurement.handlebar.x * -1} ${t('common.unit.mm')}` }}
          <span class="measurement-list-item__difference"
            >({{
              `${(measurement.handlebar.x - firstMeasurement.handlebar.x) * -1} ${t('common.unit.mm')}`
            }})</span
          ></span
        >
      </div>
      <div class="measurement-list-item__row">
        <label>{{ t('common.position.y') }}</label>
        <span
          >{{ `${measurement.handlebar.y} ${t('common.unit.mm')}` }}
          <span class="measurement-list-item__difference"
            >({{
              `${measurement.handlebar.y - firstMeasurement.handlebar.y} ${t('common.unit.mm')}`
            }})</span
          ></span
        >
      </div>
    </div>

    <div
      class="measurement-list-item__section measurement-list-item__section--crankset"
    >
      <div class="measurement-list-item__row">
        <span class="measurement-list-item__column-header">{{
          t('common.bike.crankset')
        }}</span>
      </div>
      <div class="measurement-list-item__row">
        <strong>
          <span>-</span>
        </strong>
      </div>
      <div class="measurement-list-item__row">
        <span
          v-if="measurement.crankset.active"
          class="measurement-list-item__height"
          >{{ `${measurement.crankset.active} ${t('common.unit.mm')}` }}</span
        >
        <span v-else>-</span>
      </div>
    </div>

    <div
      class="measurement-list-item__section measurement-list-item__section--pedals"
    >
      <div class="measurement-list-item__row">
        <span class="measurement-list-item__column-header">{{
          t('common.bike.pedals')
        }}</span>
      </div>
      <div class="measurement-list-item__row">
        <strong>
          <span v-if="measurement.pedal.active">
            {{ measurement.pedal.active.PEDAL_BRAND }}
            {{ measurement.pedal.active.PEDAL_MODEL }}
          </span>
          <span v-else>-</span>
        </strong>
      </div>
      <div class="measurement-list-item__row">
        <span
          v-if="measurement.pedal.active"
          class="measurement-list-item__height"
          >{{
            `${measurement.pedal.active.PEDAL_HEIGHT} ${t('common.unit.mm')}`
          }}</span
        >
        <span v-else>-</span>
      </div>
    </div>

    <div class="measurement-list-item__actions">
      <BfIconButton
        :state="index !== 0 ? ButtonState.Enabled : ButtonState.Disabled"
        :type="IconButtonType.Circular"
        icon="/icons/01-Interface Essential/23-Delete/bin-1.svg"
        @click="deleteMeasurement(index)"
        >{{ t('common.delete') }}</BfIconButton
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMeasurementStore } from '@/store/measurementStore'
import { storeToRefs } from 'pinia'
import {
  IconButtonType,
  ButtonState,
  BfIconButton,
} from '@finetic/bikefitting-ui'
import { measurementType } from '@/types/measurement'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const measurementStore = useMeasurementStore()
const { firstMeasurement } = storeToRefs(measurementStore)

withDefaults(
  defineProps<{
    index?: number
    measurement?: measurementType
  }>(),
  {
    index: 0,
    measurement: undefined,
  },
)

const deleteMeasurement = (index: number) => {
  measurementStore.delete(index)
}
</script>

<style lang="scss">
.measurement-list-item {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;
  gap: 1.2rem;
  padding: 0.8rem 1.6rem 1.6rem 1.6rem;
  border-bottom: solid 1px var(--color-dark-10);

  &:last-child {
    border-bottom: none;
  }

  &__column-header {
    font-weight: 700;
    font-size: 1rem;
    color: var(--color-dark-40);
  }

  &__section {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &--saddle {
      grid-area: 1 / 1 / 2 / 2;
    }

    &--handlebar {
      grid-area: 1 / 2 / 2 / 3;
    }

    &--crankset {
      grid-area: 2 / 1 / 3 / 2;
    }

    &--pedals {
      grid-area: 2 / 2 / 3 / 3;
    }
  }

  &__actions {
    grid-area: 1 / 3 / 3 / 4;
  }

  &__row {
    label {
      display: inline-block;
      width: 2.4rem;
      font-weight: 700;
    }
  }

  &__height {
    background-size: 16px 16px;
    background-repeat: no-repeat;
    background-position: center left;
    background-image: url(@/assets/size-y.svg);
    padding-left: 24px;
  }

  &__difference {
    color: var(--color-dark-40);
  }
}
</style>
